import { api } from './api';

// Types

// When the backend calls `.populate('schoolId', ...)` the `schoolId` field on
// a course is replaced with a populated school object. When it isn't populated
// (rare, but possible on certain paths) it stays as the raw ObjectId string.
export interface PopulatedSchool {
  _id: string;
  name: string;
  slug?: string;
  city?: string;
  country?: string;
  location?: unknown;
  phone?: string;
  email?: string;
  website?: string;
  yogaAllianceCertified?: boolean;
  approvalStatus?: string;
  isActive?: boolean;
}

export interface Course {
  _id: string;
  schoolId: string | PopulatedSchool;
  name: string;
  slug: string;
  type: 'COURSE' | 'RETREAT' | 'WORKSHOP' | 'TRAINING' | 'CLASS';
  description: string;
  shortDescription?: string;
  price: number;
  currency: string;
  duration: number; // in days
  maxStudents: number;
  minStudents?: number;
  difficulty: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'ALL_LEVELS';
  schedule?: {
    startDate?: string;
    endDate?: string;
    startDay?: number;
    activeMonths?: number[];
    monthStartDays?: { month: number; year?: number; startDay: number }[];
    dateRanges?: { startDate: string; endDate: string }[];
    timeSlots?: {
      day: string;
      startTime: string;
      endTime: string;
      label?: string;
    }[];
  };
  inclusions?: string[];
  requirements?: string[];
  whoIsThisFor?: string[];
  courseHighlights?: string[];
  whatYouWillGain?: string[];
  curriculum?: {
    title: string;
    items: string[];
  }[];
  timetable?: string[];
  media?: {
    images: string[];
    videos: string[];
  };
  accommodation?: {
    available: boolean;
    rooms: {
      roomId?: string;
      type: string;
      name?: string;
      capacity: number;
      price: number;
      bookingAmount?: number;
      amenities: string[];
      description?: string;
      images?: string[];
      videos?: string[];
      /** Admin course detail: false when linked Room is inactive or missing */
      roomIsActive?: boolean;
    }[];
    mealPlan?: {
      included: boolean;
      type: 'VEGETARIAN' | 'VEGAN' | 'OMNIVORE' | 'CUSTOM';
      description?: string;
    };
  };
  images?: string[];
  availability?: {
    monthYear: {
      month: number;
      year: number;
    }[];
    yearRound?: boolean;
    customSchedule?: {
      startMonth: number;
      endMonth: number;
      year: number;
      description?: string;
    }[];
  };
  isActive: boolean;
  isFeatured: boolean;
  rating: number;
  totalReviews: number;
  totalBookings: number;
  createdAt: string;
  updatedAt: string;
  yogaAllianceCertified?: boolean;
  createdBySource?: 'YOGA_SCHOOL' | 'BOOKMYYOGATTC';
  priceRange?: { min: number; max: number; currency: string };
  faq?: { question: string; answer: string }[];
  // Legacy alias — some callers pre-shape the course with a `school` field; we
  // still read it as a fallback if present.
  school?: PopulatedSchool;
}

// Pull out the populated school info from a course, regardless of whether it
// came back on `course.schoolId` (populated) or a legacy `course.school` alias.
export const getCourseSchool = (course: Pick<Course, 'schoolId' | 'school'>): PopulatedSchool | null => {
  if (course.school && typeof course.school === 'object') return course.school;
  if (course.schoolId && typeof course.schoolId === 'object') return course.schoolId as PopulatedSchool;
  return null;
};

export interface CourseStats {
  totalCourses: number;
  activeCourses: number;
  featuredCourses: number;
  totalBookings: number;
}

export interface GetCoursesParams {
  page?: number;
  limit?: number;
  schoolId?: string;
  type?: string;
  difficulty?: string;
  featured?: boolean;
  isActive?: boolean;
  search?: string;
  sortBy?: 'name' | 'price' | 'duration' | 'rating' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Service functions
export const courseService = {
  // Get all courses with pagination and filters
  getCourses: async (params: GetCoursesParams = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.schoolId) queryParams.append('schoolId', params.schoolId);
    if (params.type) queryParams.append('type', params.type);
    if (params.difficulty) queryParams.append('difficulty', params.difficulty);
    if (params.featured !== undefined) queryParams.append('featured', params.featured.toString());
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `/courses?${queryParams.toString()}`;
    console.log('Frontend API call URL:', url);
    console.log('Frontend query params:', queryParams.toString());
    
    const response = await api.get(url);
    return response.data;
  },

  // Get course by ID
  getCourseById: async (id: string) => {
    const response = await api.get(`/courses/${id}`);
    return response.data;
  },

  // Get courses for a specific school
  getSchoolCourses: async (schoolId: string, params: GetCoursesParams = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.type) queryParams.append('type', params.type);
    if (params.difficulty) queryParams.append('difficulty', params.difficulty);
    if (params.featured !== undefined) queryParams.append('featured', params.featured.toString());
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `/courses/school/${schoolId}?${queryParams.toString()}`;
    const response = await api.get(url);
    return response.data;
  },

  // Create new course
  createCourse: async (courseData: Partial<Course>) => {
    const response = await api.post('/courses', courseData);
    return response.data;
  },

  // Update course
  updateCourse: async (id: string, courseData: Partial<Course>) => {
    const response = await api.put(`/courses/${id}`, courseData);
    return response.data;
  },

  // Delete course
  deleteCourse: async (id: string) => {
    const response = await api.delete(`/courses/${id}`);
    return response.data;
  },

  // Toggle course status
  toggleCourseStatus: async (id: string, isActive: boolean) => {
    const response = await api.patch(`/courses/${id}/status`, { isActive });
    return response.data;
  },

  // Toggle course featured status
  toggleCourseFeatured: async (id: string, isFeatured: boolean) => {
    const response = await api.patch(`/courses/${id}/feature`, { isFeatured });
    return response.data;
  },

  // Get course statistics
  getCourseStats: async (schoolId?: string) => {
    const queryParams = new URLSearchParams();
    if (schoolId) {
      queryParams.append('schoolId', schoolId);
    }
    const url = `/courses/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  // Set default availability for existing courses
  setDefaultAvailability: async () => {
    const response = await api.post('/courses/set-default-availability');
    return response.data;
  },

  // Duplicate course (admin). Backend clones the source course, appends
  // "(Copy)" to the name, resets stats, and stamps createdBySource =
  // BOOKMYYOGATTC with the current admin id so the audit trail stays clean.
  duplicateCourse: async (id: string) => {
    const response = await api.post(`/courses/${id}/duplicate`);
    return response.data;
  },
};

// Utility functions
export const formatCourseType = (type: Course['type']): string => {
  const typeMap = {
    COURSE: 'Course',
    RETREAT: 'Retreat',
    WORKSHOP: 'Workshop',
    TRAINING: 'Training',
    CLASS: 'Class'
  } as const;
  return typeMap[type] || 'Unknown';
};

export const formatDifficulty = (difficulty: Course['difficulty']): string => {
  const difficultyMap = {
    BEGINNER: 'Beginner',
    INTERMEDIATE: 'Intermediate',
    ADVANCED: 'Advanced',
    ALL_LEVELS: 'All Levels'
  } as const;
  return difficultyMap[difficulty] || 'Unknown';
};

export const formatPrice = (price: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(price);
};

export const formatDuration = (duration: number): string => {
  if (duration === 1) return '1 day';
  if (duration < 7) return `${duration} days`;
  if (duration < 30) return `${Math.round(duration / 7)} weeks`;
  return `${Math.round(duration / 30)} months`;
};

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  return `${Math.floor(diffInSeconds / 31536000)}y ago`;
};

